import { Box, TextField } from "@mui/material";
import { IBuildlingBlockInputControl } from "./BuildingBlockInterfaces";
import { UILabel } from "./UILabel";
import { Search } from "@mui/icons-material";
import { UIStandardLayer } from "./UIStandardLayer";
import { useEffect, useState } from "react";
import { UIButton } from "./UIButton";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

export class UISearchControlItem<T> {
    name: string;
    value: T;

    constructor(name: string, value: T) {
        this.name = name;
        this.value = value;
    }
}

export interface UISearchControlProps<T> extends IBuildlingBlockInputControl<UISearchControlItem<T>[]> {
    multiSelect: boolean;
    searchTitle: string;
    noItemsText?: string;
    searchItems: (searchText: string) => Promise<UISearchControlItem<T>[]>;
}

export function UISearchControl<T>(props: UISearchControlProps<T>) {
    let [open, setOpen] = useState<boolean>(false);
    let [currentSelection, setCurrentSelection] = useState<UISearchControlItem<T>[]>([]);
    let [searchResults, setSearchResults] = useState<UISearchControlItem<T>[]>([]);
    let [val, setVal] = useState<string | undefined>();
    let [searchText, setSearchText] = useState<string>("");

    useEffect(() => {
        setCurrentSelection(props.value ?? []);
    }, [props.value]);

    useEffect(() => {
        if (props.value) {
            if (searchResults.length === 0) {
                // do empty search
                props.searchItems("").then((res) => {
                    if (!res || res.length === 0) {
                        setSearchResults(props.value ?? []);
                    } else {
                        props.value?.forEach((s) => {
                            if (!isInCurrent(s.name)) {
                                res.push(s);
                            }
                        });
                        setSearchResults(res);
                    }
                });
            }
        }
    }, [props.value]);

    function doSearch(searchText?: string) {
        setSearchText(searchText ?? "");
        setVal(searchText);
        if (searchText && searchText.length > 0) {
            props.searchItems(searchText).then((res) => {
                currentSelection.forEach((s) => {
                    if (!isInCurrent(s.name)) {
                        res.push(s);
                    }
                });
                setSearchResults(res);
            });
        } else {
            setSearchResults([]);
        }
    }

    function isInCurrent(name: string): boolean {
        return currentSelection.filter((f) => f.name === name).length !== 0;
    }

    return (
        <Box
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"start"}
            alignItems={"start"}
            width={"100%"}
        >
            <UILabel>{props.label}</UILabel>

            <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                width={"100%"}
            >
                <Box
                    border={"1px solid " + UIStyleConstants.GRAY_COLOR}
                    borderRadius="5pxpx"
                    padding="5px"
                    minWidth={"150px"}
                    width={"100%"}
                    height={"35px"}
                    onClick={() => setOpen(true)}
                >
                    {(!props.value || props.value.length === 0) && <UILabel>{props.noItemsText ?? "No items"}</UILabel>}
                    {props.value && props.value.length === 1 && <UILabel>{props.value[0].name}</UILabel>}
                    {props.value && props.value.length > 1 && <UILabel>{props.value.length} Items</UILabel>}
                </Box>

                <UIButton
                    variant="icon"
                    onClick={() => setOpen(true)}
                >
                    <Search />
                </UIButton>
            </Box>

            <UILabel variant={props.error ? "error_hint" : "hint"}>{props.error ?? props.hint}</UILabel>

            <UIStandardLayer
                primaryActionText="Done"
                onPrimaryAction={() => {
                    console.log("setting value22", currentSelection);
                    props.onChange(currentSelection);
                    setOpen(false);
                }}
                onClose={() => setOpen(false)}
                open={open}
                title={props.searchTitle}
                renderUI={() => {
                    if (props.multiSelect) {
                        return (
                            <Box
                                display={"flex"}
                                flexDirection={"column"}
                            >
                                <Box
                                    display={"flex"}
                                    flexDirection={"column"}
                                >
                                    <TextField
                                        value={val || ""}
                                        placeholder="Search..."
                                        label={"Search"}
                                        onChange={(e) => {
                                            doSearch(e.target.value);
                                        }}
                                        onBlur={(e) => {
                                            doSearch(e.target.value);
                                        }}
                                        variant="outlined"
                                        size={"small"}
                                        fullWidth={true}
                                        autoFocus={true}
                                        sx={{
                                            borderRadius: "4px",
                                            bgcolor: UIStyleConstants.GRAY_VERY_LIGHT_COLOR,
                                            marginBottom: "10px",
                                        }}
                                    />
                                    <Box
                                        display={"flex"}
                                        flexDirection={"row"}
                                    >
                                        <Box
                                            display={"flex"}
                                            flexDirection={"column"}
                                            border={"1px solid " + UIStyleConstants.GRAY_COLOR}
                                            borderRadius="4pxpx"
                                            padding={"10px"}
                                            minHeight={"400px"}
                                            width={"300px"}
                                            overflow={"auto"}
                                        >
                                            {searchResults.length === 0 && (
                                                <UILabel>[{searchText !== "" ? "No Results..." : "Search..."}]</UILabel>
                                            )}
                                            {searchResults.map((item) => {
                                                return (
                                                    <UILabel
                                                        key={"search_item-" + item.name}
                                                        onClick={() => {
                                                            let items = currentSelection;
                                                            if (props.multiSelect) {
                                                                if (!isInCurrent(item.name)) {
                                                                    items = [...currentSelection, item];
                                                                } else {
                                                                    items = currentSelection.filter(
                                                                        (i) => i.name !== item.name,
                                                                    );
                                                                }
                                                            } else {
                                                                if (
                                                                    currentSelection.length === 0 ||
                                                                    currentSelection[0].name !== item.name
                                                                ) {
                                                                    items = [item];
                                                                } else {
                                                                    items = [];
                                                                }
                                                            }

                                                            setCurrentSelection(items);
                                                        }}
                                                        color={
                                                            isInCurrent(item.name)
                                                                ? UIStyleConstants.BLUE_COLOR
                                                                : undefined
                                                        }
                                                        sx={{
                                                            fontWeight: isInCurrent(item.name) ? "bold" : undefined,
                                                        }}
                                                    >
                                                        {item.name} {isInCurrent(item.name) ? " (selected)" : ""}
                                                    </UILabel>
                                                );
                                            })}
                                        </Box>
                                        <Box
                                            width={"300px"}
                                            display={"flex"}
                                            flexDirection={"column"}
                                            marginLeft={"20px"}
                                        >
                                            <Box
                                                height={"100%"}
                                                border={"1px solid " + UIStyleConstants.GRAY_COLOR}
                                                borderRadius="4pxpx"
                                                padding={"10px"}
                                            >
                                                {currentSelection.map((item) => {
                                                    return (
                                                        <UILabel
                                                            key={"selected_item_multi-" + item.name}
                                                            onClick={() => {
                                                                let items = currentSelection;
                                                                items = items.filter((i) => i.name !== item.name);
                                                                setCurrentSelection(items);
                                                            }}
                                                            color={UIStyleConstants.BLUE_COLOR}
                                                            sx={{
                                                                fontWeight: "bold",
                                                            }}
                                                        >
                                                            {item.name}
                                                        </UILabel>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    } else {
                        return (
                            <Box
                                display={"flex"}
                                flexDirection={"column"}
                            >
                                <TextField
                                    value={val || ""}
                                    label={"Search"}
                                    onChange={(e) => {
                                        doSearch(e.target.value);
                                    }}
                                    onBlur={(e) => {
                                        doSearch(e.target.value);
                                    }}
                                    variant="outlined"
                                    size={"small"}
                                    fullWidth={true}
                                    autoFocus={true}
                                />
                                <Box
                                    display={"flex"}
                                    flexDirection={"column"}
                                    marginTop={"20px"}
                                    border={"1px solid " + UIStyleConstants.GRAY_COLOR}
                                    borderRadius="4pxpx"
                                    padding={"10px"}
                                    minHeight={"400px"}
                                    overflow={"auto"}
                                >
                                    {searchResults.length === 0 && (
                                        <UILabel>[{searchText !== "" ? "No Results..." : "Search..."}]</UILabel>
                                    )}
                                    {searchResults.map((item) => {
                                        return (
                                            <UILabel
                                                key={"search_item-" + item.name}
                                                onClick={() => {
                                                    let items = currentSelection;
                                                    if (props.multiSelect) {
                                                        if (isInCurrent(item.name)) {
                                                            items = [...currentSelection, item];
                                                        } else {
                                                            items = currentSelection.filter(
                                                                (i) => i.name !== item.name,
                                                            );
                                                        }
                                                    } else {
                                                        if (
                                                            currentSelection.length === 0 ||
                                                            currentSelection[0].name !== item.name
                                                        ) {
                                                            items = [item];
                                                        } else {
                                                            items = [];
                                                        }
                                                    }

                                                    setCurrentSelection(items);
                                                }}
                                                color={isInCurrent(item.name) ? UIStyleConstants.BLUE_COLOR : undefined}
                                                sx={{
                                                    fontWeight: isInCurrent(item.name) ? "bold" : undefined,
                                                }}
                                            >
                                                {item.name} {isInCurrent(item.name) ? " (selected)" : ""}
                                            </UILabel>
                                        );
                                    })}
                                </Box>
                            </Box>
                        );
                    }
                }}
            />
        </Box>
    );
}
